// /src/services/errorHandler.js
/**
 * Custom error handler for API responses
 * Provides standardized error handling across the application
 */

// Standard error types
export const ErrorTypes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR: 'AUTHORIZATION_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

/**
 * Formats error message based on error response
 * @param {Object} error - Axios error object
 * @returns {Object} Formatted error with type and message
 */
export const formatApiError = (error) => {
  // Default values
  let errorType = ErrorTypes.UNKNOWN_ERROR;
  let errorMessage = 'An unknown error occurred. Please try again.';
  let errorDetails = null;
  
  // No response (network error)
  if (!error.response) {
    return {
      type: ErrorTypes.NETWORK_ERROR,
      message: 'Network error. Please check your internet connection.',
      details: error.message
    };
  }
  
  // Response with status code
  const { status, data } = error.response;
  
  switch (status) {
    case 400:
      errorType = ErrorTypes.VALIDATION_ERROR;
      errorMessage = data.message || 'Invalid request. Please check your input.';
      errorDetails = data.errors || data.details;
      break;
      
    case 401:
      errorType = ErrorTypes.AUTHENTICATION_ERROR;
      errorMessage = 'Authentication required. Please login again.';
      break;
      
    case 403:
      errorType = ErrorTypes.AUTHORIZATION_ERROR;
      errorMessage = 'You do not have permission to perform this action.';
      break;
      
    case 404:
      errorType = ErrorTypes.NOT_FOUND;
      errorMessage = data.message || 'The requested resource was not found.';
      break;
      
    case 500:
    case 502:
    case 503:
    case 504:
      errorType = ErrorTypes.SERVER_ERROR;
      errorMessage = 'Server error. Please try again later.';
      break;
      
    default:
      // Use server provided message if available
      if (data && data.message) {
        errorMessage = data.message;
      }
  }
  
  return {
    type: errorType,
    message: errorMessage,
    details: errorDetails,
    status
  };
};

/**
 * Handles API errors consistently
 * @param {Object} error - Axios error object
 * @param {Function} dispatchFn - Redux dispatch function (optional)
 * @param {Function} callback - Callback function (optional)
 * @returns {Object} Formatted error
 */
export const handleApiError = (error, dispatchFn = null, callback = null) => {
  const formattedError = formatApiError(error);
  
  // Log error for debugging
  console.error('API Error:', formattedError);
  
  // 401 errors are now handled by the Axios silent refresh interceptor in api.js.
  // Only handle non-auth errors here.
  
  // Dispatch error action if Redux dispatch provided
  if (dispatchFn) {
    dispatchFn({
      type: 'app/setError',
      payload: formattedError
    });
  }
  
  // Execute callback if provided
  if (callback && typeof callback === 'function') {
    callback(formattedError);
  }
  
  return formattedError;
};

export default {
  ErrorTypes,
  formatApiError,
  handleApiError
};
